<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<script>
import { onMount } from 'svelte';
import InfoTooltip from '../../components/InfoTooltip.svelte';
import uiStore from '../../utils/UIStore';
import { snapValues } from './MapSliderValue.js';
import { pageAnnouncement } from './ScreenreaderText.js';

export let sliderValue;

// text read to screenreader users
let ariaText = pageAnnouncement(sliderValue);
// time to debounce the update of the aria text
let timer;
// update the ariaText with a timeout / debouncing
$: {
  clearTimeout(timer);
  timer = setTimeout(() => {
    // debouncing: 150ms
    ariaText = pageAnnouncement(sliderValue);
  }, 150);
}

onMount(() => {
  const labelsContainer = document.getElementById('sliderLabels');
  if (labelsContainer) {
    for (let i = 0; i < 6; i += 1) {
      const label = document.createElement('span');
      let labelText = 0;
      switch (i) {
        case 0:
          labelText = '<1/2';
          break;
        case 1:
          labelText = '1/2';
          break;
        case 2:
          labelText = '1';
          break;
        case 3:
          labelText = '2';
          break;
        case 4:
          labelText = '5';
          break;
        case 5:
          labelText = '10';
          break;
      }
      label.innerText = labelText;
      labelsContainer.appendChild(label);
    }
  }
});

function snap(current, direction) {
  const index = snapValues.findIndex((v) => v >= current - 0.001);
  let newIndex = 0;

  if (snapValues[index] === current) {
    newIndex = direction === 'up' ? index + 1 : index - 1;
  } else {
    newIndex = direction === 'up' ? index : index - 1;
  }

  newIndex = Math.max(0, Math.min(newIndex, snapValues.length - 1));
  return snapValues[newIndex];
}

function handleKeydown(event) {
  const isUp = event.key === 'ArrowRight' || event.key === 'ArrowUp';
  const isDown = event.key === 'ArrowLeft' || event.key === 'ArrowDown';

  if (!isUp && !isDown) return;

  event.preventDefault();

  if (event.ctrlKey) {
    // --- continous mode (Strg + Pfeil) ---
    const step = 0.01;
    let newValue = sliderValue;

    if (isUp) {
      newValue += step;
    } else {
      newValue -= step;
    }

    newValue = Math.max(0, Math.min(1, newValue));
    sliderValue = Math.round(newValue * 100) / 100;
  } else {
    // --- SNAP MODUS (Nur Pfeil) ---
    if (isUp) {
      sliderValue = snap(sliderValue, 'up');
    } else {
      sliderValue = snap(sliderValue, 'down');
    }
  }
}

let uiTexts;
if (uiStore && uiStore.textStore) {
  uiStore.textStore.subscribe((store) => {
    uiTexts = store.texts;
  });
}
</script>

<div class="slider-container">
  <div style="display:flex; gap: 5px;">
    <span>{uiTexts?.lengthSlider?.label || 'Label'}</span>
    <InfoTooltip
      infoText={uiTexts?.lengthSlider?.tooltip || ''}
    />
  </div>
  <div class="slider-wrapper">
    <input
      type="range"
      class="slider"
      min="0"
      max="1"
      step="0.01"
      bind:value={sliderValue}
      on:keydown={handleKeydown}
      aria-valuetext={ariaText}
    />
  </div>
  <div class="slider-labels" id="sliderLabels"></div>
</div>

<style>
  .slider-container {
    position: relative;
    width: 400px;
  }
  .slider-wrapper {
    position: relative;
  }

  .slider-wrapper::before {
    content: "";
    position: absolute;
    top: calc(50% + 3px);
    left: 10px;
    right: 10px;
    height: 2px;
    background-color: black;
    transform: translateY(-50%);
    z-index: 0;
  }

  .slider {
    width: 100%;
    margin: 0;
    padding: 0px;
    box-sizing: border-box;
    background: transparent;
    position: relative;
    z-index: 10;
  }

  .slider::-webkit-slider-thumb,
  .slider::-webkit-slider-thumb:active,
  .slider:focus::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: var(--sec-color);
    cursor: pointer;
    z-index: 1;
    outline: 2px solid white;
  }

  .slider::-moz-range-thumb,
  .slider:active::-moz-range-thumb,
  .slider:focus::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: var(--sec-color);
    cursor: pointer;
    outline: 1px solid white;
  }

  .slider-labels {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: space-between;
  }

  input:focus {
    outline: 2px solid #000;
    outline-offset: 10px;
  }
</style>